import React, { useState, useEffect, useRef } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign, 
  ShoppingCart, 
  Target, 
  Activity, 
  Globe, 
  Download, 
  RefreshCw,
  Calendar,
  Filter,
  Eye,
  Clock,
  Percent,
  CreditCard,
  Building
} from 'lucide-react';
import { 
  getAnalyticsDashboard, 
  formatCurrency, 
  formatPercentage, 
  exportAnalyticsData,
  calculateGrowthRate,
  generateChartData
} from '../lib/analytics';
import { getFeeSettings } from '../lib/admin';
import KPICard from '../components/analytics/KPICard';
import LineChart from '../components/analytics/LineChart';
import BarChart from '../components/analytics/BarChart';
import PieChart from '../components/analytics/PieChart';
import DateRangePicker from '../components/analytics/DateRangePicker';
import FilterPanel from '../components/analytics/FilterPanel';
import type { AnalyticsDashboardData, DashboardFilters, DateRange } from '../types/analytics';

export default function AnalyticsDashboard() {
  const [data, setData] = useState<AnalyticsDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [platformFeeRate, setPlatformFeeRate] = useState(0.0131); // Default 1.31%
  const [gatewayFeeRate, setGatewayFeeRate] = useState(0.0269); // Default 2.69%
  const sparklesRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useState<DashboardFilters>({
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    category: 'all',
    userSegment: 'all',
    region: 'all'
  });

  useEffect(() => {
    loadDashboardData();
    loadFeeRates();
    
    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [filters]);

  const loadFeeRates = async () => {
    try {
      const { platformFeeRate: pfr, gatewayFeeRate: gfr } = await getFeeSettings();
      setPlatformFeeRate(pfr);
      setGatewayFeeRate(gfr);
    } catch (error) {
      console.error('Error loading fee rates:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const dashboardData = await getAnalyticsDashboard(filters);
      setData(dashboardData);
    } catch (err) {
      console.error('Error loading analytics dashboard:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    await loadFeeRates();
    setRefreshing(false);
  };

  const handleExport = () => {
    if (data) {
      exportAnalyticsData(data, 'analytics_dashboard');
    }
  };

  const handleDateRangeChange = (dateRange: DateRange) => {
    setFilters({ ...filters, dateRange });
  };

  const handleFiltersChange = (newFilters: DashboardFilters) => {
    setFilters(newFilters);
  };

  const resetFilters = () => {
    setFilters({
      dateRange: {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      },
      category: 'all',
      userSegment: 'all',
      region: 'all'
    });
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center space-x-2">
                <BarChart3 className="w-8 h-8" />
                <span>Analytics Dashboard</span>
              </h1>
              <p className="text-gray-300 mt-2">
                Comprehensive business metrics and performance indicators
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <DateRangePicker
                value={filters.dateRange}
                onChange={handleDateRangeChange}
              />
              
              <FilterPanel
                filters={filters}
                onChange={handleFiltersChange}
                onReset={resetFilters}
                isOpen={showFilters}
                onToggle={() => setShowFilters(!showFilters)}
              />

              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>

              <button
                onClick={handleExport}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {data && (
            <>
              {/* Real-time Stats Bar */}
              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Activity className="w-5 h-5 text-green-400" />
                    <span className="text-white font-medium">Real-time Stats</span>
                  </div>
                  <div className="flex items-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-gray-300">
                        {data.realTimeStats.activeUsers} active users
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ShoppingCart className="w-4 h-4 text-blue-400" />
                      <span className="text-gray-300">
                        {data.realTimeStats.recentTransactions} recent transactions
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        data.realTimeStats.systemHealth === 'good' ? 'bg-green-400' :
                        data.realTimeStats.systemHealth === 'warning' ? 'bg-yellow-400' :
                        'bg-red-400'
                      }`}></div>
                      <span className="text-gray-300">
                        System {data.realTimeStats.systemHealth}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fee Structure Summary */}
              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Transaction Fee Structure</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-900/30 border border-blue-800 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <Building className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-200 font-medium">Platform Fee</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-300">{(platformFeeRate * 100).toFixed(2)}%</p>
                    <p className="text-blue-200 text-sm mt-1">Our service fee</p>
                  </div>
                  
                  <div className="bg-purple-900/30 border border-purple-800 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <CreditCard className="w-5 h-5 text-purple-400" />
                      <span className="text-purple-200 font-medium">Gateway Fee</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-300">{(gatewayFeeRate * 100).toFixed(2)}%</p>
                    <p className="text-purple-200 text-sm mt-1">Payment processing fee</p>
                  </div>
                  
                  <div className="bg-green-900/30 border border-green-800 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <Percent className="w-5 h-5 text-green-400" />
                      <span className="text-green-200 font-medium">Total Fee</span>
                    </div>
                    <p className="text-2xl font-bold text-green-300">{((platformFeeRate + gatewayFeeRate) * 100).toFixed(2)}%</p>
                    <p className="text-green-200 text-sm mt-1">Combined transaction fee</p>
                  </div>
                </div>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard
                  title="Daily Revenue"
                  value={formatCurrency(data.kpis.total_revenue.daily)}
                  change={calculateGrowthRate(data.kpis.total_revenue.daily, data.kpis.total_revenue.daily * 0.9)}
                  changeType="increase"
                  icon={<DollarSign className="w-5 h-5" />}
                  description="Today's total revenue"
                />
                
                <KPICard
                  title="Monthly Revenue"
                  value={formatCurrency(data.kpis.total_revenue.monthly)}
                  change={calculateGrowthRate(data.kpis.total_revenue.monthly, data.kpis.total_revenue.monthly * 0.85)}
                  changeType="increase"
                  icon={<TrendingUp className="w-5 h-5" />}
                  description="This month's revenue"
                />

                <KPICard
                  title="Active Users"
                  value={data.kpis.active_users.toLocaleString()}
                  change={data.kpis.customer_acquisition_rate}
                  changeType="increase"
                  icon={<Users className="w-5 h-5" />}
                  description="Currently active users"
                />

                <KPICard
                  title="Conversion Rate"
                  value={formatPercentage(data.kpis.conversion_rates.payment_success)}
                  change={5.2}
                  changeType="increase"
                  icon={<Target className="w-5 h-5" />}
                  description="Payment success rate"
                />

                <KPICard
                  title="Average Order Value"
                  value={formatCurrency(data.kpis.average_order_value)}
                  change={2.8}
                  changeType="increase"
                  icon={<ShoppingCart className="w-5 h-5" />}
                  description="Average transaction value"
                />

                <KPICard
                  title="Customer LTV"
                  value={formatCurrency(data.kpis.customer_lifetime_value)}
                  change={8.5}
                  changeType="increase"
                  icon={<Users className="w-5 h-5" />}
                  description="Customer lifetime value"
                />

                <KPICard
                  title="Daily Active Users"
                  value={data.engagement.daily_active_users.toLocaleString()}
                  change={3.2}
                  changeType="increase"
                  icon={<Activity className="w-5 h-5" />}
                  description="Users active today"
                />

                <KPICard
                  title="Session Duration"
                  value={`${Math.round(data.engagement.session_duration / 60)}m`}
                  change={-2.1}
                  changeType="decrease"
                  icon={<Clock className="w-5 h-5" />}
                  description="Average session length"
                />
              </div>

              {/* Revenue and User Trends */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <LineChart
                  data={generateChartData(
                    data.trends.revenue,
                    data.trends.revenue.map(t => t.period),
                    'value'
                  )}
                  title="Revenue Trend"
                  height={300}
                />

                <LineChart
                  data={generateChartData(
                    data.trends.users,
                    data.trends.users.map(t => t.period),
                    'value'
                  )}
                  title="User Growth"
                  height={300}
                />
              </div>

              {/* Fee Distribution */}
              <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-white mb-4">Fee Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <Building className="w-5 h-5 text-blue-400" />
                      <span className="text-white font-medium">Platform Revenue</span>
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {formatCurrency(data.financial.revenue_breakdown.platform_fees)}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {(platformFeeRate * 100).toFixed(2)}% of transaction volume
                    </p>
                  </div>
                  
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <CreditCard className="w-5 h-5 text-purple-400" />
                      <span className="text-white font-medium">Gateway Costs</span>
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {formatCurrency(data.financial.operating_costs)}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {(gatewayFeeRate * 100).toFixed(2)}% of transaction volume
                    </p>
                  </div>
                  
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <DollarSign className="w-5 h-5 text-green-400" />
                      <span className="text-white font-medium">Net Profit</span>
                    </div>
                    <p className="text-2xl font-bold text-white">
                      {formatCurrency(data.financial.revenue_breakdown.platform_fees - data.financial.operating_costs)}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {formatPercentage(data.financial.profit_margins)} profit margin
                    </p>
                  </div>
                </div>
              </div>

              {/* Engagement Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">User Engagement</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Daily Active Users</span>
                      <span className="text-white font-medium">
                        {data.engagement.daily_active_users.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Monthly Active Users</span>
                      <span className="text-white font-medium">
                        {data.engagement.monthly_active_users.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Bounce Rate</span>
                      <span className="text-white font-medium">
                        {formatPercentage(data.engagement.bounce_rate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Avg Session Duration</span>
                      <span className="text-white font-medium">
                        {Math.round(data.engagement.session_duration / 60)}m
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Retention Rates</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Day 1</span>
                      <span className="text-white font-medium">
                        {formatPercentage(data.engagement.retention_rates.day_1)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Day 7</span>
                      <span className="text-white font-medium">
                        {formatPercentage(data.engagement.retention_rates.day_7)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Day 30</span>
                      <span className="text-white font-medium">
                        {formatPercentage(data.engagement.retention_rates.day_30)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Feature Usage</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Registry Creation</span>
                      <span className="text-white font-medium">
                        {data.engagement.feature_usage.registry_creation.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Gift Claiming</span>
                      <span className="text-white font-medium">
                        {data.engagement.feature_usage.gift_claiming.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Group Gifting</span>
                      <span className="text-white font-medium">
                        {data.engagement.feature_usage.group_gifting.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">RSVP Responses</span>
                      <span className="text-white font-medium">
                        {data.engagement.feature_usage.rsvp_responses.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PieChart
                  data={{
                    labels: ['Registries', 'Group Gifts', 'Platform Fees'],
                    datasets: [{
                      data: [
                        data.financial.revenue_breakdown.registries,
                        data.financial.revenue_breakdown.group_gifts,
                        data.financial.revenue_breakdown.platform_fees
                      ],
                      backgroundColor: ['#003049', '#D62828', '#F77F00']
                    }]
                  }}
                  title="Revenue Breakdown"
                  size={250}
                />

                <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Financial Health</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Operating Costs</span>
                      <span className="text-white font-medium">
                        {formatCurrency(data.financial.operating_costs)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Profit Margin</span>
                      <span className="text-green-400 font-medium">
                        {formatPercentage(data.financial.profit_margins)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Payment Success Rate</span>
                      <span className="text-white font-medium">
                        {formatPercentage(data.financial.payment_success_rate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Refund Rate</span>
                      <span className="text-red-400 font-medium">
                        {formatPercentage(data.financial.refund_rate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Pending Withdrawals</span>
                      <span className="text-yellow-400 font-medium">
                        {formatCurrency(data.financial.pending_withdrawals)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Total Withdrawn</span>
                      <span className="text-white font-medium">
                        {formatCurrency(data.financial.total_withdrawn)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BarChart
                  data={{
                    labels: ['New Users', 'Returning Users', 'Power Users'],
                    datasets: [{
                      data: [
                        data.customer.segmentation.new_users,
                        data.customer.segmentation.returning_users,
                        data.customer.segmentation.power_users
                      ],
                      backgroundColor: 'rgba(0, 48, 73, 0.8)'
                    }]
                  }}
                  title="User Segmentation"
                  height={300}
                />

                <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-white mb-4">Geographic Distribution</h3>
                  <div className="space-y-3">
                    {data.customer.geographic_distribution.slice(0, 5).map((location, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <Globe className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-300">{location.country}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-medium">{location.users} users</div>
                          <div className="text-sm text-gray-400">
                            {formatCurrency(location.revenue)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Conversion Funnel */}
              <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Conversion Funnel</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">
                      {formatPercentage(data.kpis.conversion_rates.registry_creation)}
                    </div>
                    <div className="text-gray-300">Registry Creation</div>
                    <div className="text-sm text-gray-400">Visitors → Registry Creators</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">
                      {formatPercentage(data.kpis.conversion_rates.gift_claiming)}
                    </div>
                    <div className="text-gray-300">Gift Claiming</div>
                    <div className="text-sm text-gray-400">Viewers → Gift Claimers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">
                      {formatPercentage(data.kpis.conversion_rates.payment_success)}
                    </div>
                    <div className="text-gray-300">Payment Success</div>
                    <div className="text-sm text-gray-400">Attempts → Completed</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}